import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Wrench, Package, Calculator, Plus, RefreshCcw, Pencil, Trash2,
  Undo2, AlertTriangle, HardHat, Truck, Sparkles,
} from "lucide-react";

const TIPO_META = {
  MATERIALE:         { label: "Materiale",  icon: Package, color: "text-amber-600" },
  MANODOPERA:        { label: "Manodopera", icon: HardHat, color: "text-blue-600" },
  ATTREZZATURA_NOLO: { label: "Noli/Attr.", icon: Wrench,  color: "text-purple-600" },
  TRASPORTO:         { label: "Trasporto",  icon: Truck,   color: "text-cyan-600" },
  ALTRO:             { label: "Altro",      icon: Sparkles,color: "text-slate-500" },
};

const FILTRI = [
  { key: "all",   label: "Tutte" },
  { key: "MATERIALE", label: "Materiali" },
  { key: "MANODOPERA", label: "Manodopera" },
  { key: "ATTREZZATURA_NOLO", label: "Noli/Attrezzature" },
  { key: "TRASPORTO", label: "Trasporto" },
  { key: "ALTRO", label: "Altro" },
];

const fmt = (v) => v == null ? "—" : Number(v).toLocaleString("it-IT",
  { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const fmtEur = (v) => v == null ? "—" : `€ ${Number(v).toLocaleString("it-IT",
  { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Sezione RISORSE CALCOLATE per una riga computo (FASE C).
 * Va mostrata SOLO se esiste un mapping analisi attivo (`hasMappingAttivo`).
 */
export default function RigaMaterialiCalcolatiPanel({ riga, canEdit = true,
                                                      hasMappingAttivo = false,
                                                      onRefreshMapping = null }) {
  const [state, setState] = useState({ materiali: [], quantita_computo_corrente: null,
    quantita_computo_riferimento: null, necessario_ricalcolo: false });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);

  const rigaId = riga?.id;

  const load = async () => {
    if (!rigaId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/righe/${rigaId}/materiali-calcolati`);
      setState(data);
    } catch (e) {
      // 404 se riga non trovata
      setState({ materiali: [], quantita_computo_corrente: null,
        quantita_computo_riferimento: null, necessario_ricalcolo: false });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rigaId]);

  const filtered = useMemo(() => {
    if (filter === "all") return state.materiali;
    return (state.materiali || []).filter(m => m.tipo_risorsa === filter);
  }, [state.materiali, filter]);

  const totali = useMemo(() => {
    const t = {};
    for (const m of state.materiali || []) {
      const tp = m.tipo_risorsa || "ALTRO";
      t[tp] = (t[tp] || 0) + (Number(m.importo_calc) || 0);
    }
    return t;
  }, [state.materiali]);

  const importoTot = useMemo(
    () => (state.materiali || []).reduce((s, m) => s + (Number(m.importo_calc) || 0), 0),
    [state.materiali]
  );

  const openPreview = async (opts = { rigenera: false }) => {
    if (!hasMappingAttivo) {
      toast.error("Nessuna analisi collegata alla riga.");
      return;
    }
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const { data } = await api.get(`/righe/${rigaId}/materiali-calcolati/preview`);
      setPreview({ ...data, _rigenera: opts.rigenera });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmGenera = async () => {
    try {
      await api.post(`/righe/${rigaId}/materiali-calcolati/genera`, {
        disattiva_precedenti: true,
        mantieni_override: true,
      });
      toast.success("Risorse calcolate generate");
      setPreviewOpen(false);
      setPreview(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const saveEdit = async () => {
    if (!editRow) return;
    try {
      const payload = {
        descrizione_effettiva: editRow.descrizione_effettiva,
        codice_risorsa_effettivo: editRow.codice_risorsa_effettivo,
        unita_misura: editRow.unita_misura,
        incidenza_effettiva: editRow.incidenza_effettiva !== "" && editRow.incidenza_effettiva != null
          ? Number(editRow.incidenza_effettiva) : null,
        quantita_totale_calc: editRow._qta_edited && editRow.quantita_totale_calc !== ""
          ? Number(editRow.quantita_totale_calc) : undefined,
        prezzo_unitario_effettivo: editRow.prezzo_unitario_effettivo !== "" && editRow.prezzo_unitario_effettivo != null
          ? Number(editRow.prezzo_unitario_effettivo) : null,
        tipo_risorsa: editRow.tipo_risorsa,
      };
      await api.patch(`/materiali-calcolati/${editRow.id}`, payload);
      toast.success("Risorsa aggiornata");
      setEditRow(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const doDelete = async (m) => {
    if (!confirm(`Disattivare la risorsa "${m.descrizione_effettiva}"?`)) return;
    try {
      await api.delete(`/materiali-calcolati/${m.id}`);
      toast.success("Risorsa disattivata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const doRipristina = async (m) => {
    if (!confirm("Ripristinare i valori originali dall'analisi? L'override manuale verrà annullato.")) return;
    try {
      await api.post(`/materiali-calcolati/${m.id}/ripristina`);
      toast.success("Valori ripristinati");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  if (!hasMappingAttivo) {
    return (
      <Card><CardContent className="p-4 text-sm text-slate-500">
        Collega prima un'analisi prezzo per poter generare le risorse calcolate.
      </CardContent></Card>
    );
  }

  if (loading) return <div className="text-slate-500 p-4 text-sm">Caricamento risorse…</div>;

  const empty = (state.materiali || []).length === 0;

  return (
    <div className="space-y-3" data-testid="riga-materiali-calcolati">
      {/* Header + azioni */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-600 font-semibold">Risorse calcolate</div>
          <div className="text-[11px] text-slate-500">
            Quantità computo:{" "}
            <span className="font-mono">{fmt(state.quantita_computo_corrente)} {riga?.unita_misura || ""}</span>
            {state.quantita_computo_riferimento != null &&
             state.quantita_computo_riferimento !== state.quantita_computo_corrente && (
              <> · <span className="text-amber-600">Riferimento ultima generazione: {fmt(state.quantita_computo_riferimento)}</span></>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && empty && (
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={() => openPreview({ rigenera: false })}
              data-testid="mat-genera-btn">
              <Calculator className="h-4 w-4 mr-1" /> Genera risorse
            </Button>
          )}
          {canEdit && !empty && (
            <Button size="sm" variant="outline" onClick={() => openPreview({ rigenera: true })}
              data-testid="mat-rigenera-btn">
              <RefreshCcw className="h-4 w-4 mr-1" /> Ricalcola dall'analisi
            </Button>
          )}
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setManualOpen(true)}
              data-testid="mat-aggiungi-manuale-btn">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi risorsa
            </Button>
          )}
        </div>
      </div>

      {state.necessario_ricalcolo && (
        <div className="p-3 rounded-md border border-amber-400 bg-amber-50/70 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-sm flex items-start gap-2"
             data-testid="mat-necessario-ricalcolo">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            Quantità computo modificata rispetto all'ultima generazione. Le risorse non-override
            NON sono state ricalcolate automaticamente: usa "Ricalcola dall'analisi" per aggiornarle
            (gli override manuali saranno mantenuti).
          </div>
        </div>
      )}

      {!empty && (
        <>
          {/* Filtri */}
          <div className="flex flex-wrap gap-1">
            {FILTRI.map(f => (
              <Button key={f.key} size="sm"
                variant={filter === f.key ? "default" : "outline"}
                className={filter === f.key ? "bg-slate-900 text-white hover:bg-slate-800" : ""}
                onClick={() => setFilter(f.key)}
                data-testid={`mat-filter-${f.key}`}>
                {f.label}
              </Button>
            ))}
          </div>

          {/* Tabella */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[110px]">Tipo</TableHead>
                  <TableHead>Risorsa</TableHead>
                  <TableHead className="w-[70px]">UM</TableHead>
                  <TableHead className="text-right w-[100px]">Incidenza</TableHead>
                  <TableHead className="text-right w-[100px]">Q.tà computo</TableHead>
                  <TableHead className="text-right w-[110px]">Q.tà calcolata</TableHead>
                  <TableHead className="text-right w-[100px]">Prezzo</TableHead>
                  <TableHead className="text-right w-[110px]">Importo</TableHead>
                  <TableHead className="w-[130px] text-center">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(m => {
                  const meta = TIPO_META[m.tipo_risorsa] || TIPO_META.ALTRO;
                  const Icon = meta.icon;
                  return (
                    <TableRow key={m.id}
                      className={`${m.is_manual_override ? "bg-amber-50/70 dark:bg-amber-900/10" : ""} ${m.da_verificare ? "border-l-4 border-l-rose-500" : ""}`}
                      data-testid={`mat-row-${m.id}`}>
                      <TableCell>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${meta.color}`}>
                          <Icon className="h-3.5 w-3.5" />
                          {meta.label}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium">{m.descrizione_effettiva}</div>
                        <div className="text-[10px] text-slate-500 flex gap-2 items-center flex-wrap">
                          {m.codice_risorsa_effettivo && <span>cod. {m.codice_risorsa_effettivo}</span>}
                          {m.is_manual_override && <Badge variant="outline" className="text-amber-700 border-amber-500 text-[9px] uppercase">Override</Badge>}
                          {m.da_verificare && <Badge variant="outline" className="text-rose-700 border-rose-500 text-[9px] uppercase">Da verificare</Badge>}
                          {m.note && !m.is_manual_override && <span className="italic">{m.note}</span>}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{m.unita_misura || "—"}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmt(m.incidenza_effettiva)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{fmt(m.quantita_computo_source)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{fmt(m.quantita_totale_calc)}</TableCell>
                      <TableCell className="text-right font-mono text-xs">{m.prezzo_unitario_effettivo != null ? fmtEur(m.prezzo_unitario_effettivo) : "—"}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{fmtEur(m.importo_calc)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {canEdit && (
                            <>
                              <Button size="icon" variant="ghost" title="Modifica"
                                onClick={() => setEditRow({ ...m, _qta_edited: false })}
                                data-testid={`mat-edit-${m.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              {m.is_manual_override && m.risorsa_id_source && (
                                <Button size="icon" variant="ghost" title="Ripristina da analisi"
                                  onClick={() => doRipristina(m)}
                                  data-testid={`mat-ripristina-${m.id}`}>
                                  <Undo2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                              <Button size="icon" variant="ghost" title="Disattiva"
                                className="text-rose-600 hover:text-rose-700"
                                onClick={() => doDelete(m)}
                                data-testid={`mat-delete-${m.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Totale */}
          <div className="flex items-center justify-end gap-4 text-xs">
            {Object.entries(totali).map(([t, v]) => (
              <div key={t} className="text-slate-500">
                <span className="mr-1">{TIPO_META[t]?.label || t}:</span>
                <span className="font-mono font-semibold text-slate-700 dark:text-slate-200">{fmtEur(v)}</span>
              </div>
            ))}
            <div className="text-slate-700 dark:text-slate-200">
              <span className="uppercase tracking-widest text-[10px] mr-1">Totale</span>
              <span className="font-mono font-bold text-base">{fmtEur(importoTot)}</span>
            </div>
          </div>
        </>
      )}

      {/* Dialog preview → conferma generazione */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="mat-preview-dialog">
          <DialogHeader>
            <DialogTitle>{preview?._rigenera ? "Ricalcola risorse dall'analisi" : "Anteprima risorse calcolate"}</DialogTitle>
            <div className="text-xs text-slate-500 mt-1">
              Nessuna scrittura in DB finché non confermi. Gli override manuali esistenti verranno preservati.
            </div>
          </DialogHeader>
          {previewLoading ? (
            <div className="text-slate-500 py-6 text-sm text-center">Caricamento…</div>
          ) : preview ? (
            <div className="space-y-3">
              {(preview.warnings || []).length > 0 && (
                <div className="p-2 rounded border border-amber-400 bg-amber-50/70 dark:bg-amber-900/20 text-amber-800 dark:text-amber-200 text-xs space-y-1">
                  {preview.warnings.map((w, i) => <div key={i} className="flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}</div>)}
                </div>
              )}
              <div className="border rounded overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Risorsa</TableHead>
                      <TableHead>UM</TableHead>
                      <TableHead className="text-right">Incidenza</TableHead>
                      <TableHead className="text-right">Q.tà computo</TableHead>
                      <TableHead className="text-right">Q.tà calcolata</TableHead>
                      <TableHead className="text-right">Prezzo</TableHead>
                      <TableHead className="text-right">Importo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(preview.materiali || []).map((m, i) => (
                      <TableRow key={i} className={m.da_verificare ? "border-l-4 border-l-rose-500" : ""}>
                        <TableCell className="text-xs">{TIPO_META[m.tipo_risorsa]?.label || m.tipo_risorsa}</TableCell>
                        <TableCell className="text-sm">
                          <div>{m.descrizione_effettiva}</div>
                          {m.da_verificare && <div className="text-[10px] text-rose-700">Da verificare: {m.motivo_verifica}</div>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{m.unita_misura || "—"}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{fmt(m.incidenza_effettiva)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{fmt(m.quantita_computo_source)}</TableCell>
                        <TableCell className="text-right text-sm font-mono font-semibold">{fmt(m.quantita_totale_calc)}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{fmtEur(m.prezzo_unitario_effettivo)}</TableCell>
                        <TableCell className="text-right text-sm font-mono font-semibold">{fmtEur(m.importo_calc)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPreviewOpen(false); setPreview(null); }} data-testid="mat-preview-annulla">Annulla</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={confirmGenera} disabled={previewLoading}
              data-testid="mat-preview-conferma">
              <Calculator className="h-4 w-4 mr-1" /> Conferma generazione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog EDIT */}
      <Dialog open={!!editRow} onOpenChange={(o) => { if (!o) setEditRow(null); }}>
        <DialogContent className="max-w-lg" data-testid="mat-edit-dialog">
          <DialogHeader><DialogTitle>Modifica risorsa</DialogTitle></DialogHeader>
          {editRow && (
            <div className="space-y-3 text-sm">
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">Non altera l'analisi prezzo originale.</div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500">Descrizione</label>
                <Input value={editRow.descrizione_effettiva || ""} onChange={(e) => setEditRow({ ...editRow, descrizione_effettiva: e.target.value })} data-testid="mat-edit-descrizione" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Codice</label>
                  <Input value={editRow.codice_risorsa_effettivo || ""} onChange={(e) => setEditRow({ ...editRow, codice_risorsa_effettivo: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">UM</label>
                  <Input value={editRow.unita_misura || ""} onChange={(e) => setEditRow({ ...editRow, unita_misura: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Tipo</label>
                  <Select value={editRow.tipo_risorsa} onValueChange={(v) => setEditRow({ ...editRow, tipo_risorsa: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIPO_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Incidenza</label>
                  <Input type="number" step="0.000001"
                    value={editRow.incidenza_effettiva ?? ""}
                    onChange={(e) => setEditRow({ ...editRow, incidenza_effettiva: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Q.tà tot</label>
                  <Input type="number" step="0.0001"
                    value={editRow.quantita_totale_calc ?? ""}
                    onChange={(e) => setEditRow({ ...editRow, quantita_totale_calc: e.target.value, _qta_edited: true })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-500">Prezzo unit.</label>
                  <Input type="number" step="0.0001"
                    value={editRow.prezzo_unitario_effettivo ?? ""}
                    onChange={(e) => setEditRow({ ...editRow, prezzo_unitario_effettivo: e.target.value })} />
                </div>
              </div>
              <div className="text-[10px] text-slate-500 italic">
                Modifica incidenza → ricalcola quantità. Modifica manuale della quantità → sovrascrive.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditRow(null)}>Annulla</Button>
            <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold"
              onClick={saveEdit} data-testid="mat-edit-salva">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog risorsa manuale */}
      <ManualDialog open={manualOpen} onClose={() => setManualOpen(false)}
        riga={riga} onSaved={load} />
    </div>
  );
}


function ManualDialog({ open, onClose, riga, onSaved }) {
  const [form, setForm] = useState({
    tipo_risorsa: "MATERIALE", descrizione_effettiva: "",
    codice_risorsa_effettivo: "", unita_misura: "",
    incidenza_effettiva: "", quantita_totale_calc: "",
    prezzo_unitario_effettivo: "",
  });
  useEffect(() => {
    if (open) setForm({
      tipo_risorsa: "MATERIALE", descrizione_effettiva: "",
      codice_risorsa_effettivo: "", unita_misura: "",
      incidenza_effettiva: "", quantita_totale_calc: "",
      prezzo_unitario_effettivo: "",
    });
  }, [open]);

  const submit = async () => {
    if (!form.descrizione_effettiva?.trim()) { toast.error("Descrizione obbligatoria"); return; }
    try {
      await api.post(`/righe/${riga.id}/materiali-calcolati/manuale`, {
        tipo_risorsa: form.tipo_risorsa,
        descrizione_effettiva: form.descrizione_effettiva.trim(),
        codice_risorsa_effettivo: form.codice_risorsa_effettivo?.trim() || null,
        unita_misura: form.unita_misura?.trim() || null,
        incidenza_effettiva: form.incidenza_effettiva !== "" ? Number(form.incidenza_effettiva) : null,
        quantita_totale_calc: form.quantita_totale_calc !== "" ? Number(form.quantita_totale_calc) : null,
        prezzo_unitario_effettivo: form.prezzo_unitario_effettivo !== "" ? Number(form.prezzo_unitario_effettivo) : null,
      });
      toast.success("Risorsa aggiunta");
      onClose();
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="mat-manual-dialog">
        <DialogHeader><DialogTitle>Aggiungi risorsa manuale</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Tipo</label>
              <Select value={form.tipo_risorsa} onValueChange={(v) => setForm({ ...form, tipo_risorsa: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TIPO_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Codice</label>
              <Input value={form.codice_risorsa_effettivo} onChange={(e) => setForm({ ...form, codice_risorsa_effettivo: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">UM</label>
              <Input value={form.unita_misura} onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Descrizione *</label>
            <Input value={form.descrizione_effettiva} onChange={(e) => setForm({ ...form, descrizione_effettiva: e.target.value })} data-testid="mat-manual-descrizione" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Incidenza</label>
              <Input type="number" step="0.000001" value={form.incidenza_effettiva} onChange={(e) => setForm({ ...form, incidenza_effettiva: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Q.tà tot</label>
              <Input type="number" step="0.0001" value={form.quantita_totale_calc} onChange={(e) => setForm({ ...form, quantita_totale_calc: e.target.value })} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Prezzo unit.</label>
              <Input type="number" step="0.0001" value={form.prezzo_unitario_effettivo} onChange={(e) => setForm({ ...form, prezzo_unitario_effettivo: e.target.value })} />
            </div>
          </div>
          <div className="text-[10px] text-slate-500 italic">
            Se lasci vuota la quantità e inserisci l'incidenza, verrà calcolata come q.computo × incidenza.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-950 font-semibold" onClick={submit} data-testid="mat-manual-salva">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
